import { Alert, AlertType } from "shared-components/toast";
import { Worklist } from "../shared/interfaces";
import { is12WPlate, is24WPlate, is96WPlate } from "../shared/PlateHelpers";
import {
  extractPlateCode,
  getColIndexFromSelectedWell,
  getRowIndexFromSelectedWell,
} from "../shared/WorklistHelpers";
import { DEST_PLATE_CODES } from "./constants/destPlateCodes";
import { DestTransferStrategy } from "./NucleofectionInterfaces";
import {
  CompoundPlateInfoState,
  DestPlateInfoState,
  IntermediatePlateInfoState,
  NucleofectionDestPlateLabwareTypeInfo,
  NucleofectionPlateInfoState,
  SourcePlateInfoState,
  WorklistWellMappingState,
} from "./state";

export const buildCompoundPlateWorklist = (
  compoundPlateInfo: CompoundPlateInfoState[],
  sourcePlateInfo: SourcePlateInfoState[]
) => {
  const worklist: Worklist[] = [];
  if (compoundPlateInfo[0] && sourcePlateInfo[0]) {
    worklist.push({
      task: "CompoundPlate",
      details: "Stamp",
      aspirateVol: "",
      dispenseVol: "",
      transferVol: compoundPlateInfo[0].transferVol,
      sourcePlateType: compoundPlateInfo[0].labwareTypeCode,
      sourcePlateBarcode: compoundPlateInfo[0].plateBarcode,
      sourceWellID: "",
      destinationPlateType: sourcePlateInfo[0].labwareTypeCode,
      destinationPlateBarcode: sourcePlateInfo[0].plateBarcode,
      destinationWellID: "",
    });
  }
  return worklist;
};

export const buildSrcToNucWorklist = (
  compoundPlateInfo: CompoundPlateInfoState[],
  sourcePlateInfo: SourcePlateInfoState[],
  nucPlateInfo: NucleofectionPlateInfoState[],
  sourceToNuc: WorklistWellMappingState[]
) => {
  const worklist: Worklist[] = [];
  for (const row of sourceToNuc) {
    const currentSourcePlate = sourcePlateInfo[0];
    const currentDestPlate = nucPlateInfo[0];
    const index = currentSourcePlate.wellInfo.findIndex(
      (e: any) =>
        e.rowPos - 1 === getRowIndexFromSelectedWell(row.sourceWellId) &&
        e.colPos - 1 === getColIndexFromSelectedWell(row.sourceWellId)
    );
    if (index > -1)
      worklist.push({
        task: "SrcToNuc",
        details: "",
        aspirateVol: "",
        dispenseVol: "",
        transferVol: row.transferVol,
        sourcePlateType: currentSourcePlate.labwareTypeCode,
        sourcePlateBarcode: currentSourcePlate.plateBarcode,
        sourceWellID: row.sourceWellId,
        destinationPlateType: currentDestPlate.labwareTypeCode,
        destinationPlateBarcode: currentDestPlate.plateBarcode,
        destinationWellID: row.destWellId,
      });
  }
  return worklist;
};

export const buildNucToIntWorklist = (
  nucPlateInfo: NucleofectionPlateInfoState[],
  intPlateInfo: IntermediatePlateInfoState[],
  nucToInt: WorklistWellMappingState[]
) => {
  const worklist: Worklist[] = [];
  for (const row of nucToInt) {
    const currentSourcePlate = nucPlateInfo[0];
    const currentDestPlate = intPlateInfo[0];
    const index = currentSourcePlate.wellInfo.findIndex(
      (e: any) =>
        e.rowPos - 1 === getRowIndexFromSelectedWell(row.sourceWellId) &&
        e.colPos - 1 === getColIndexFromSelectedWell(row.sourceWellId)
    );
    if (index > -1)
      worklist.push({
        task: "NucToInt",
        details: `TopUpVol|${currentSourcePlate.topUpVol};`,
        aspirateVol: "",
        dispenseVol: "",
        transferVol: "",
        sourcePlateType: currentSourcePlate.labwareTypeCode,
        sourcePlateBarcode: currentSourcePlate.plateBarcode,
        sourceWellID: row.sourceWellId,
        destinationPlateType: currentDestPlate.labwareTypeCode,
        destinationPlateBarcode: currentDestPlate.plateBarcode,
        destinationWellID: row.destWellId,
      });
  }
  return worklist;
};

export const buildIntToDestWorklist = (
  destPlateInfo: DestPlateInfoState[],
  intPlateInfo: IntermediatePlateInfoState[],
  intToDest: WorklistWellMappingState[]
) => {
  const worklist: Worklist[] = [];
  for (const plate of destPlateInfo) {
    const is96WellDestPlate = is96WPlate(plate.rows.length, plate.cols.length);
    if (is96WellDestPlate) {
      const currentSourcePlate = intPlateInfo[0];
      const currentDestPlate = destPlateInfo[plate.index];

      //transferVol same for all intToDest mappings (stamp)
      const stampTransferVol = intToDest.find(
        (wellMapping) => wellMapping.destPlateIndex === plate.index
      )?.transferVol;
      if (currentSourcePlate && currentDestPlate) {
        worklist.push({
          task: "IntToDest",
          details: `Preprocess|${currentDestPlate.preprocess ? 1 : 0};Topup|${
            currentDestPlate.topup ? 1 : 0
          };Stamp|1;`,
          aspirateVol: "",
          dispenseVol: "",
          transferVol: stampTransferVol,
          sourcePlateType: currentSourcePlate.labwareTypeCode,
          sourcePlateBarcode: currentSourcePlate.plateBarcode,
          sourceWellID: "",
          destinationPlateType: currentDestPlate.labwareTypeCode,
          destinationPlateBarcode: currentDestPlate.plateBarcode,
          destinationWellID: "",
        });
      }
    }
  }

  for (const row of intToDest) {
    const destPlate = destPlateInfo[row.destPlateIndex];
    const is96WellDestPlate = is96WPlate(
      destPlate.rows.length,
      destPlate.cols.length
    );

    if (is96WellDestPlate) continue; // skip all 96w plates, already pushed to worklist
    const destPlateIndex = destPlateInfo.findIndex(
      (item) => item.index === row.destPlateIndex
    );
    const currentSourcePlate = intPlateInfo[0];
    const currentDestPlate = destPlateInfo[destPlateIndex];
    if (currentSourcePlate && currentDestPlate) {
      worklist.push({
        task: "IntToDest",
        details: `Preprocess|${currentDestPlate.preprocess ? 1 : 0};Topup|${
          currentDestPlate.topup ? 1 : 0
        };`,
        aspirateVol: "",
        dispenseVol: "",
        transferVol: row.transferVol?.toString() || "",
        sourcePlateType: currentSourcePlate.labwareTypeCode,
        sourcePlateBarcode: currentSourcePlate.plateBarcode,
        sourceWellID: row.sourceWellId,
        destinationPlateType: currentDestPlate.labwareTypeCode,
        destinationPlateBarcode: currentDestPlate.plateBarcode,
        destinationWellID: row.destWellId,
      });
    }
  }
  return worklist;
};

export const buildNucWashWorklist = (
  nucPlateInfo: NucleofectionPlateInfoState[]
): Worklist[] => {
  return [
    {
      task: "NucWash",
      details: `WashCycles|${nucPlateInfo[0].numberOfWashes};WashVol|${nucPlateInfo[0].washVol};`,
      aspirateVol: "",
      dispenseVol: "",
      transferVol: "",
      sourcePlateType: "",
      sourcePlateBarcode: "",
      sourceWellID: "",
      destinationPlateType: "",
      destinationPlateBarcode: "",
      destinationWellID: "",
    },
  ];
};

export const buildMappingFileWorklist = (
  compoundPlateInfo: CompoundPlateInfoState[],
  sourcePlateInfo: SourcePlateInfoState[],
  nucPlateInfo: NucleofectionPlateInfoState[],
  intPlateInfo: IntermediatePlateInfoState[],
  destPlateInfo: DestPlateInfoState[],
  destSampleType: string
) => {
  const worklist: Worklist[] = [];
  const uniqueCompoundPlateCode = extractPlateCode(
    compoundPlateInfo[0]?.plateBarcode
  );
  const uniqueSourcePlateCode = extractPlateCode(
    sourcePlateInfo[0]?.plateBarcode
  );
  const uniqueNucPlateCode = extractPlateCode(nucPlateInfo[0]?.plateBarcode);
  const uniqueIntermediatePlateCode = extractPlateCode(
    intPlateInfo[0]?.plateBarcode
  );
  const uniqueDestPlateCodes = [
    ...new Set(
      destPlateInfo
        .map((e) => extractPlateCode(e.plateBarcode))
        .filter((labware) => labware !== "")
    ),
  ];

  const cpdPlateCode = uniqueCompoundPlateCode;
  const srcPlateCode = uniqueSourcePlateCode;
  const nucPlateCode = uniqueNucPlateCode;
  const intPlateCode = uniqueIntermediatePlateCode;
  const destPlateCode =
    uniqueDestPlateCodes.length > 1 ? "Y" : uniqueDestPlateCodes[0];
  worklist.push({
    task: "AuxToSrcMappingFileKeyword",
    details: "_" + cpdPlateCode + "_to_" + srcPlateCode + "_",
  });
  worklist.push({
    task: "SrcToInt1MappingFileKeyword",
    details: "_" + srcPlateCode + "_to_" + nucPlateCode + "_",
  });
  worklist.push({
    task: "Int1ToInt2MappingFileKeyword",
    details: "_" + nucPlateCode + "_to_" + intPlateCode + "_",
  });
  worklist.push({
    task: "Int2ToDestMappingFileKeyword",
    details: "_" + intPlateCode + "_to_" + destPlateCode + "_",
  });
  if (destSampleType && destSampleType !== "None") {
    worklist.push({
      task: "DestMappingFileAppendation",
      details: `_FORCE_SAMPLETYPE_${destSampleType}`,
    });
  }

  return worklist;
};

export const getIntTotalVol = (
  nucTopUpVol: number,
  washVol: number,
  numberOfWashes: number
) => {
  return nucTopUpVol + washVol * numberOfWashes;
};

export const getIntVolRemaining = (
  intWellId: string,
  intTotalVol: number,
  intToDest: WorklistWellMappingState[]
) => {
  let intVolUsed = 0;
  for (const wellMapping of intToDest) {
    if (wellMapping.sourceWellId === intWellId) {
      const transferVol = Number(wellMapping?.transferVol) || 0;
      intVolUsed += transferVol;
    }
  }

  const volRemaining = intTotalVol - intVolUsed;
  return volRemaining;
};

export const anyPlatesNot96Well = (destPlateInfo: DestPlateInfoState[]) => {
  const destPlatesWithData = destPlateInfo.filter(
    (destPlate) => destPlate.labwareTypeCode !== ""
  );
  return destPlatesWithData.some(
    (destPlate) => !is96WPlate(destPlate.rows.length, destPlate.cols.length)
  );
};

export const updateDestTransferStratIfNon96W = (
  destPlateInfo: DestPlateInfoState[],
  destTransferStrategy: DestTransferStrategy,
  updateMethodSettings: (key: string, value: string | number) => void
) => {
  if (
    anyPlatesNot96Well(destPlateInfo) &&
    destTransferStrategy !== DestTransferStrategy.Custom
  ) {
    Alert({
      type: AlertType.WARNING,
      message:
        "You have non 96w Dest plates. DestTransferStrategy set to Custom.",
    });
    updateMethodSettings("destTransferStrategy", DestTransferStrategy.Custom);
  }
};

export const twelve24WBefore96WDest = (
  newPlateTypeInfo: NucleofectionDestPlateLabwareTypeInfo,
  newDestPlateIndex: number,
  destPlateInfo: DestPlateInfoState[]
) => {
  if (
    !is96WPlate(
      newPlateTypeInfo.plateRows?.length ?? 0,
      newPlateTypeInfo.plateCols?.length ?? 0
    )
  )
    return false;

  const first1224Index = destPlateInfo.findIndex((destPlate) => {
    const numRows = destPlate.rows?.length;
    const numCols = destPlate.cols?.length;
    return is12WPlate(numRows, numCols) || is24WPlate(numRows, numCols);
  });

  if (first1224Index === -1) return false;

  return first1224Index < newDestPlateIndex;
};

export const getDestPlateCode = (destPlate: DestPlateInfoState) => {
  const numWells = destPlate.cols.length * destPlate.rows.length;
  let destPlateCode;
  switch (numWells) {
    case 96:
      destPlateCode = DEST_PLATE_CODES[numWells];
      break;
    case 24:
      destPlateCode = DEST_PLATE_CODES[numWells];
      break;
    case 12:
      destPlateCode = DEST_PLATE_CODES[numWells];
      break;
  }
  return destPlateCode;
};
